import {
  Box,
  Button,
  IconButton,
  OsModal,
  Typography,
} from "@outschool/backpack";
import { ClassPost_CommentFragment } from "@outschool/gql-frontend-generated";
import { faTrashAlt } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import {
  ClassPostCommentContent,
  UserAvatar,
} from "@outschool/ui-components-classroom";
import React from "react";

const RECENT_COMMENT_WINDOW_MS = 6 * 24 * 60 * 60 * 1000;

function formatCommentTime(sendAt: Date, timeZone?: string) {
  const isRecent =
    Math.abs(Date.now() - new Date(sendAt).getTime()) <
    RECENT_COMMENT_WINDOW_MS;
  return isRecent
    ? `${Time.formatWeekday(sendAt, timeZone)} ${Time.formatTime(
        sendAt,
        timeZone,
        false,
        true,
      )}`
    : Time.formatDateTimeWithWeekday(sendAt, timeZone);
}

export function ClassDiscussionCard({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation("learnerApp\\ClassDiscussion");
  return (
    <Box
      data-test-id="ClassDiscussion"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        borderRadius: 16,
        paddingX: 32,
        paddingY: 20,
        backgroundColor: "common.white",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t("Class discussion")}
      </Typography>
      {children}
    </Box>
  );
}

export function ClassDiscussionComment({
  comment,
  isEducator,
  isSender,
  timeZone,
  onDelete,
  trackingMetadata,
}: {
  comment: ClassPost_CommentFragment;
  isEducator: boolean;
  isSender: boolean;
  timeZone?: string;
  onDelete?: () => Promise<void>;
  trackingMetadata?: Record<string, string | number>;
}) {
  const { t } = useTranslation("learnerApp\\ClassDiscussion");
  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
  const senderName = comment.sender.name?.trim() ?? "";

  return (
    <Box
      data-test-id="ClassDiscussionComment"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        "& .class-discussion-delete": { opacity: 0 },
        "&:hover .class-discussion-delete, &:focus-within .class-discussion-delete":
          { opacity: 1 },
        "@media (hover: none)": {
          "& .class-discussion-delete": { opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          padding: 2,
          borderRadius: "50%",
          border: "2px solid",
          borderColor: isSender ? "primary.500" : "transparent",
        }}
      >
        <UserAvatar sender={comment.sender} size={32} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 8,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {isSender ? t("You") : senderName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
            }}
          >
            {isEducator && (
              <Box
                component="span"
                sx={{
                  paddingX: 6,
                  paddingY: 1,
                  borderRadius: 4,
                  backgroundColor: "neutral.100",
                  color: "neutral.600",
                  fontSize: 12,
                  lineHeight: "18px",
                }}
              >
                {t("Teacher")}
              </Box>
            )}
            <Typography variant="body2" sx={{ color: "neutral.500" }}>
              {formatCommentTime(comment.sendAt, timeZone)}
            </Typography>
          </Box>
        </Box>
        <ClassPostCommentContent
          plain
          sender={comment.sender}
          messageContent={comment.messageContent}
          isEducator={isEducator}
          isSender={isSender}
          trackingLabel="classroom_comment"
          trackingMetadata={trackingMetadata}
        />
      </Box>
      {onDelete && (
        <>
          <Box className="class-discussion-delete">
            <IconButton
              icon={faTrashAlt}
              variant="text"
              aria-label={t("Delete comment")}
              onClick={() => setIsConfirmingDelete(true)}
            />
          </Box>
          <OsModal
            open={isConfirmingDelete}
            onClose={() => setIsConfirmingDelete(false)}
            title={t("Confirm comment deletion")}
            horizontalActionAlignment="right"
            actionSecondary={
              <Button onClick={() => setIsConfirmingDelete(false)}>
                {t("Cancel")}
              </Button>
            }
            actionPrimary={
              <Button
                onClick={async () => {
                  setIsConfirmingDelete(false);
                  await onDelete();
                }}
              >
                {t("Confirm")}
              </Button>
            }
          >
            <Typography variant="body1" component="p">
              {t(
                "This comment will be removed from the class discussion for everyone. This can’t be undone.",
              )}
            </Typography>
          </OsModal>
        </>
      )}
    </Box>
  );
}
